import axios, { Method, AxiosResponse } from 'axios'
import Cookies from 'js-cookie'
import notification from '../components/Notification'
import * as Sentry from '@sentry/react'

export type FileType = {
  field: string
  item: File | File[]
}

type CheckStatusType = {
  response: {
    status: number
    data: ResponseType
    config: any
    headers: any
  }
  config: any
  headers: any
  status: number
  data: any
}

type ErrorType = {
  response:
    | {
        code: string
        apiError: string | JsonType
        apiTextError: string
      }
    | {
        code?: number
        apiError?: string
        apiTextError?: string
      }
  message: string
}

type JsonType = {
  response: {
    apiError: string
  }
  errors: string
  message: string
}

export const checkStatus = (resp: CheckStatusType) => {
  const { REACT_APP_MAX_MEGABYTE_SIZE_UPLOAD } = process.env
  const response = resp.status ? resp : resp.response
  return new Promise((resolve, reject) => {
    const error: ErrorType = <any>new Error()
    error.response = {}
    error.response.code = response.status

    if ([401, 403].includes(response.status)) {
      error.message = 'У вас недостаточно прав для выполнения этого действия'
    } else if (response.status >= 400 && response.status < 500) {
      console.log('Error: ', response)
      if (response.status === 413) {
        notification(
          'error',
          '',
          `Размер файла превышает ${REACT_APP_MAX_MEGABYTE_SIZE_UPLOAD} МБ`
        )
      } else {
        error.message = `Некорректные данные: ${response.status}`
      }
    } else if (response.status >= 500) {
      if (response.status === 501) {
        Sentry.setContext('error', {
          data: JSON.stringify(response.data),
          config: JSON.stringify(response.config),
          headers: response.headers,
        })
      }
      if (response.status === 504) {
        notification(
          'error',
          '',
          'Слишком долгое время ожидания ответа, попробуйте позднее'
        )
      } else {
        error.message = 'Ошибка сервера, попробуйте позднее'
      }
    } else {
      resolve(response.data)
      return
    }

    new Promise(res => {
      res(response.data)
    }).then((json: any) => {
      error.response.apiError = json
      if (typeof json?.errors === 'object') {
        error.response.apiTextError = Object.values(json.errors).join(', ')
      } else if (typeof json?.message === 'string') {
        error.response.apiTextError = json?.message
      }
      reject(error)
    })
  })
}

export const request = async (
  endpoint: string,
  method: Method,
  params?: object,
  data?: any,
  fileData?: FileType,
  uploadProgress?: (percent: number) => void
): Promise<AxiosResponse> => {
  const accessToken = Cookies.get('token')

  const formData = new FormData()
  if (fileData && fileData.item) {
    if (Array.isArray(fileData.item)) {
      for (const item of fileData.item) {
        if (fileData.item) {
          formData.append(
            `${fileData.field}[${fileData.item.findIndex(i => i === item)}]`,
            item
          )
        }
      }
    } else {
      formData.append(fileData.field, fileData.item)
    }

    for (const item in data) {
      if (data[item]) {
        formData.append(item, data[item])
      }
    }
  }

  return new Promise((resolve, reject) => {
    axios({
      baseURL: process.env.REACT_APP_API_URL,
      method,
      headers: {
        Accept: 'application/json',
        Authorization: accessToken ? `Bearer ${accessToken}` : '',
        'Content-Type':
          fileData && fileData.item ? 'multipart/form-data' : undefined,
      },
      onUploadProgress: function (progressEvent: {
        loaded: number
        total: number
      }) {
        if (uploadProgress) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          )
          uploadProgress(percentCompleted)
        }
      },
      url: (process.env.REACT_APP_API_URI_PREFIX || '') + endpoint,
      params,
      data: ['POST', 'PATCH'].includes(method)
        ? fileData && fileData.item
          ? formData
          : data
        : null,
      validateStatus: () => {
        return true
      },
    })
      .then((response: any) => checkStatus(response))
      .then(response => response)
      .then((response: any) => resolve(response))
      .catch(error => {
        throw error
      })
      .catch(error => reject(error))
  })
}

export const get = (url: string, params?: object): Promise<AxiosResponse> =>
  request(url, 'GET', params)
export const put = (url: string, data?: object): Promise<AxiosResponse> =>
  request(url, 'PUT', {}, data)
export const del = (url: string, params?: object): Promise<AxiosResponse> =>
  request(url, 'DELETE', params)
export const post = (
  url: string,
  data?: object,
  params?: object
): Promise<AxiosResponse> => request(url, 'POST', params, data)
export const patch = (url: string, data?: object): Promise<AxiosResponse> =>
  request(url, 'PATCH', {}, data)
export const upload = (
  url: string,
  file: FileType,
  data?: object,
  params?: object,
  uploadProgress?: (percent: number) => void
): Promise<AxiosResponse> =>
  request(url, 'POST', params, data, file, uploadProgress)
